<script setup>
import { CheckCircleIcon } from '@heroicons/vue/20/solid';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import Trans from '@/i18n/translation';
import Price from '@/components/Price.vue';
import { multiply, pipe } from 'ramda';

const props = defineProps({
  plan: {
    type: Object,
    required: true,
  },
  useCallback: {
    required: false,
    default: false,
  },
});

defineEmits(['planChosen']);
const redirectToMail = () => {
  window.location.href = 'mailto:sales@gamebook.services';
};

const roundToTwoDecimals = (value) => Math.round(value * 100) / 100;

const applyVat = multiply(1 + 0.19);

const finalPrice = pipe(applyVat, roundToTwoDecimals);

// eslint-disable-next-line max-len
const getSubscriptionPrice = computed(() => (props.plan.requireCompany ? finalPrice(props.plan.seatPrice) : finalPrice(props.plan.subscriptionPrice)));

const { t, tm } = useI18n({ messages: props.plan.description });
</script>

<template>
  <div class="base-gradient ring-1 ring-basecolor p-8 text-center text-xs rounded-lg text-primary">
    <h3
      :id="props.plan.id"
      class="text-lg font-bold"
    >
      {{ t('planName') }}
    </h3>

    <div class="rounded-2xl bg-primary text-basecolor w-10 m-auto text-2xs">
      {{ props.plan.seats }}
    </div>

    <p class="mt-6 mb-2">
      <span
        v-if="!props.plan.needsContact || props.plan.isDemo"
        class="inline-flex items-center justify-center w-full gap-x-2 text-2xl font-bold tracking-tight"
      >
        <Price :price="getSubscriptionPrice" />
        <span
          v-if="!props.plan.isDemo && !props.plan.needsContact"
          class="text-sm font-light"
        >
          {{ props.plan.requireCompany
            ? t('landingPage.prices.perSeatPerYear')
            : t('landingPage.prices.perYear') }}
        </span>
      </span>

      <span
        v-else
        class="flex justify-center text-sm font-bold tracking-tight"
      >
        {{ t('landingPage.prices.contactForPricing') }}
      </span>
    </p>

    <button
      v-if=" props.useCallback === true"
      @click="$emit('planChosen', props.plan)"
      class="w-full btn-cta m-auto block py-2"
    >
      {{ $t('registerView.choosePlan') }}
    </button>

    <RouterLink
      v-else-if="!props.plan.needsContact && !props.plan.requireCompany"
      :to="Trans.i18nRoute({ name: 'register', query: { plan: props.plan.name } })"
      v-slot="{ navigate }"
    >
      <button
        class="w-full btn-cta m-auto block font-bold py-2"
        @click="navigate"
      >
        {{ $t('landingPage.startNow') }}
      </button>
    </RouterLink>
    <RouterLink
      v-else-if="props.plan.isDemo"
      :to="Trans.i18nRoute({ name: 'register', query: { plan: props.plan.name } })"
      v-slot="{ navigate }"
    >
      <button
        class="w-full btn-cta m-auto block py-2"
        @click="navigate"
      >
        {{ $t('landingPage.freeTrialBtn') }}
      </button>
    </RouterLink>
    <RouterLink
      v-else-if="props.plan.requireCompany && !props.plan.needsContact"
      :to="Trans.i18nRoute({ name: 'register', query: { plan: props.plan.name } })"
      v-slot="{ navigate }"
    >
      <button
        class="w-full btn-cta m-auto block py-2"
        @click="navigate"
      >
        {{ $t('landingPage.startCompany') }}
      </button>
    </RouterLink>

    <RouterLink
      v-else
      :to="Trans.i18nRoute({ name: 'contact' })"
      class="w-full btn-cta m-auto block text-balance py-2"
    >
      {{ $t('landingPage.contactUs') }}
    </RouterLink>

    <p class="mt-4 text-left leading-6">
      {{ t('planDescription') }}
    </p>

    <ul
      role="list"
      class="mt-8 space-y-3 leading-6"
    >
      <li
        v-for="feature in tm('planFeatures')"
        :key="feature"
        class="flex gap-x-3 text-left"
      >
        <CheckCircleIcon
          class="h-6 w-5 flex-none fill-secondary"
          aria-hidden="true"
        />
        {{ feature }}
      </li>
    </ul>
  </div>
</template>

<style scoped></style>
